import flow from 'lodash/flow';
import { lazyAfterPaint } from 'react-loosely-lazy';
import type { PreloadedQuery } from 'react-relay';
import {
	AnalyticsSource,
	ViewTracker,
	ReduxAnalyticsData,
	AnalyticsSubject,
} from '@atlassian/jira-analytics-web-react/src';
import { SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux';
import type { viewBoardCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBoardCustomFiltersQuery.graphql';
import { AddBoardToHistory } from '@atlassian/jira-view-history';
import { isIssueModalShowingSelector } from '../state/selectors/issue/issue-modal-selectors';
import { getInsightsPanel } from '../state/selectors/panels';
import { getCurrentUserAccountId } from '../state/selectors/people/people-selectors';
import {
	contextPathSelector,
	rapidViewIdSelector,
	projectIdSelector,
	projectKeySelector,
	getIsCMPBoard,
	projectTypeSelector,
	getBoardTypeForAnalytics,
	getIssueLimitExceeded,
	getSavedFilterId,
} from '../state/selectors/software/software-selectors';
import { getSelectedOrAllSprintsSelector } from '../state/selectors/sprint/sprint-selectors';
import { getSwimlaneMode } from '../state/selectors/swimlane/swimlane-mode-selectors';
import { getEstimationStatistic } from '../state/selectors/work/work-selectors';
import type { State } from '../state/types';
import BoardWithDragSync from './board-with-drag-sync';
import DetailView from './detail-view';
import DocumentTitle from './document-title';
import { withViewExperienceTracker } from './experience-tracker';
import Flags from './flags';
import GlobalSearchTracker from './global-search-tracker';
import Header from './header';
import AsyncPrefetchIssues from './prefetch-issues/async';
import Realtime from './realtime';
import Board from './view';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AfterPaintModals = lazyAfterPaint(
	() => import(/* webpackChunkName: "async-board-modals" */ './modals'),
	{ ssr: false },
);

type OwnProps = {
	isCacheHit: boolean;
	customFiltersQueryRef: PreloadedQuery<viewBoardCustomFiltersQuery> | null | undefined;
	rapidViewId: string;
	xsrfToken: string;
	hasMinimap: boolean;
	hasModals: boolean;
	hasFlags: boolean;
	projectId: number;
};

const App = flow(
	connect(
		(state: State, ownProps: OwnProps) => ({
			isCacheHit: ownProps.isCacheHit,
			customFiltersQueryRef: ownProps.customFiltersQueryRef,
			Header,
			Modals: AfterPaintModals,
			Flags,
			Board: BoardWithDragSync,
			ViewTracker,
			DocumentTitle,
			PrefetchIssues: AsyncPrefetchIssues,
			DetailView,
			Realtime,
			AddBoardToHistory,
			GlobalSearchTracker,
			isInsightsPanelOpen: getInsightsPanel(state),
			baseUrl: contextPathSelector(state),
			projectKey: projectKeySelector(state),
			estimationStatistic: getEstimationStatistic(state),
			currentUserAccountId: getCurrentUserAccountId(state),
			issueLimitExceeded: getIssueLimitExceeded(state),
			savedFilterId: getSavedFilterId(state),
			selectedSprintsOrAllSprints: getSelectedOrAllSprintsSelector(state),
		}),
		{},
	),
	AnalyticsSource('board', SCREEN),
	AnalyticsSubject('board'),
	ReduxAnalyticsData((state) => ({
		containerType: 'project',
		containerId: String(projectIdSelector(state)),
		attributes: {
			projectId: projectIdSelector(state),
			boardId: rapidViewIdSelector(state),
			isIssueDetailVisible: isIssueModalShowingSelector(state),
			isUIF: true,
			isClassic: getIsCMPBoard(state),
			isInactiveTab: document.hidden,
			boardType: getBoardTypeForAnalytics(state),
			projectType: projectTypeSelector(state),
			swimlaneMode: getSwimlaneMode(state),
		},
	})),
)(Board);

export default withViewExperienceTracker<OwnProps>(App);
